import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { snapshotFilter } from "@/tools/create-snapshot/create-snapshot-tree-filter";
import { CustomDatesCache } from "@/utils/tree-generation/custom-date-utils";
import { elementToTreeData, TreeData } from "@/utils/tree-generation/tree-data";
import { isElementNodeToHide } from "@/utils/tree-generation/tree-discriminators";
import {
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { IElementsRecord } from "@faro-lotv/project-source";
import { partition } from "es-toolkit";

type GenerateSnapshotTreeParams = {
  /** ID of the parent of the current recursion level */
  parentId: GUID;

  /** All IElements of the project */
  elements: LightweightElementsRecord | IElementsRecord;

  /** Id of the tag identifying rooms copied for video mode datasets */
  videoModeCopyTagId?: GUID;
};

/**
 * @returns A representation of the project tree useful to present data for the snapshot tool.
 */
export function generateSnapshotTree({
  parentId,
  elements,
  videoModeCopyTagId,
}: GenerateSnapshotTreeParams): TreeData[] {
  const customDatesCache = new CustomDatesCache();

  const tree = generateFilteredTree(
    parentId,
    elements,
    videoModeCopyTagId,
    customDatesCache,
  );

  return tree;
}

/**
 * Generates the project tree with date, text, and tags filtering applied recursively
 *
 * @param parentId ID of the parent of the current recursion level
 * @param elements All IElements of the project
 * @param videoModeCopyTagId Data that stay constant during the tree generation
 * @param customDatesCache Cache for custom dates to avoid redundant calculations
 * @returns The filtered tree representation
 */
function generateFilteredTree(
  parentId: GUID,
  elements: IElementsRecord | LightweightElementsRecord,
  videoModeCopyTagId: GUID | undefined,
  customDatesCache: CustomDatesCache,
): TreeData[] {
  const subtree: TreeData[] = [];

  const parentElement = elements[parentId];
  if (!parentElement) return subtree;

  // The elements of the current level of recursion.
  const childrenIds = [...(parentElement.childrenIds ?? [])];

  for (const elementId of childrenIds) {
    const element = elements[elementId];
    if (
      !element ||
      isElementNodeToHide(element, elements, videoModeCopyTagId)
    ) {
      continue;
    }

    subtree.push(
      ...processChildren(
        element,
        elements,
        parentElement,
        videoModeCopyTagId,
        customDatesCache,
      ),
    );
  }
  return subtree;
}

function processChildren(
  element: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
  parentElement: LightweightElement | IElement,
  videoModeCopyTagId: GUID | undefined,
  customDatesCache: CustomDatesCache,
): TreeData[] {
  const customDate = customDatesCache.customDates.get(element.id);

  const filterResult = snapshotFilter(
    element,
    parentElement.type,
    videoModeCopyTagId,
    customDate,
  );

  let children: TreeData[] = [];
  if (filterResult.shouldKeepChildren) {
    children = generateFilteredTree(
      element.id,
      elements,
      videoModeCopyTagId,
      customDatesCache,
    );
  }

  // A group or time series should not be visible if they're empty
  if (shouldHideIfEmpty(element)) {
    filterResult.shouldKeepElement &&= children.length > 0;
  }

  if (filterResult.shouldKeepElement) {
    // Move the 4D Sessions folder in front so that the project structure
    // will always be
    // - Area
    //    - 4D Sessions
    //      - VideoMode 1
    //      - [...]
    //      - VideoMode M
    //    - Room 1
    //    - [...]
    //    - Room N
    const [pcs, rest] = partition(
      children,
      (node) => node.element?.typeHint === IElementTypeHint.dataSession,
    );
    children = [...pcs, ...rest];

    return [
      elementToTreeData({
        element,
        parentElement,
        children,
        createdAt: customDate,
        label: filterResult.displayName ?? element.name,
      }),
    ];
  } else if (filterResult.shouldKeepChildren) {
    return children;
  }

  return [];
}

function shouldHideIfEmpty(element: LightweightElement | IElement): boolean {
  return (
    element.type === IElementType.group ||
    element.type === IElementType.timeSeries ||
    (element.type === IElementType.section &&
      element.typeHint === IElementTypeHint.area)
  );
}
